import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError } from '../notify';


// The creator card: who the creator is. Discovery, auto-fill and Generate Week
// keep to subs for their photos and audience; descriptions, titles and
// comments are written as them. Blank = a woman whose content is for straight
// men (the tool before cards).
const SEX = { woman: 'Woman', man: 'Man', trans_woman: 'Trans woman', trans_man: 'Trans man' };
const AUDIENCE = { straight_men: 'Straight men', gay_men: 'Gay men', women: 'Women' };
const FACE = { shown: 'Face shown', hidden: 'Faceless', both: 'Face shown and hidden' };
const EXPLICIT = { 'non-nude': 'Non-nude', 'semi-nude': 'Up to semi-nude', nude: 'Up to nude', explicit: 'Up to explicit' };
const SOLO = { solo: 'Solo', couple: 'Couple', both: 'Solo and couple' };
const CARD_KEYS = ['sex', 'audience', 'age', 'face', 'max_explicitness', 'solo_couple'];
const cardOf = (c) => Object.fromEntries(CARD_KEYS.map(k => [k, c[k] ?? (k === 'audience' ? [] : '')]));
const SUGGEST_LABEL = { sex: SEX, face: FACE, max_explicitness: EXPLICIT };
const FIELD_NAME = { sex: 'Sex', face: 'Face', max_explicitness: 'Most explicit' };

function Select({ value, options, onChange, blank }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ flex: 'none', minWidth: '150px' }}>
      <option value="">{blank}</option>
      {Object.entries(options).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
    </select>
  );
}

function CreatorCard({ creator, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [card, setCard] = useState(cardOf(creator));
  const [saving, setSaving] = useState(false);
  const [vault, setVault] = useState(null);
  const [reading, setReading] = useState(false);
  // the saved card after a save (kept mounted, so the vault suggestions stay)
  useEffect(() => { if (!editing) setCard(cardOf(creator)); }, [creator, editing]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const body = { ...patch };
      if ('age' in body) body.age = body.age === '' ? null : Number(body.age);
      await axios.put(`${API}/api/creators/${creator.id}`, body);
      setEditing(false);
      onSaved();
    } catch (err) {
      notifyError(err, 'Could not save the creator card');
    }
    setSaving(false);
  };
  const readVault = async () => {
    setReading(true);
    try {
      const { data } = await axios.get(`${API}/api/creators/${creator.id}/card-suggestions`);
      setVault(data);
    } catch (err) {
      notifyError(err, 'Could not read the vault');
    }
    setReading(false);
  };
  const toggleAudience = (a) => setCard({ ...card, audience: card.audience.includes(a) ? card.audience.filter(x => x !== a) : [...card.audience, a] });

  const facts = [
    SEX[creator.sex],
    (creator.audience || []).length ? `content for ${creator.audience.map(a => AUDIENCE[a].toLowerCase()).join(' + ')}` : null,
    creator.age ? `${creator.age}` : null,
    FACE[creator.face], EXPLICIT[creator.max_explicitness], SOLO[creator.solo_couple]
  ].filter(Boolean);
  // Suggestions that differ from the card (a person accepts each)
  const open = vault ? Object.entries(vault.suggestions || {}).filter(([k, sg]) => creator[k] !== sg.value) : [];

  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '12px', color: '#aaaaaa', marginBottom: '6px' }}>Creator card: who they are, so subs, descriptions, titles and comments fit them</div>
      {editing ? (
        <div className="form-grid">
          <div className="form-row" style={{ flexWrap: 'wrap' }}>
            <Select value={card.sex} options={SEX} blank="Sex…" onChange={v => setCard({ ...card, sex: v })} />
            <input type="number" min="18" max="99" placeholder="Age" value={card.age} onChange={e => setCard({ ...card, age: e.target.value })} style={{ flex: 'none', width: '90px' }} />
            <Select value={card.face} options={FACE} blank="Face…" onChange={v => setCard({ ...card, face: v })} />
            <Select value={card.max_explicitness} options={EXPLICIT} blank="Most explicit…" onChange={v => setCard({ ...card, max_explicitness: v })} />
            <Select value={card.solo_couple} options={SOLO} blank="Solo / couple…" onChange={v => setCard({ ...card, solo_couple: v })} />
          </div>
          <div className="form-row" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="sub" style={{ fontSize: '13px' }}>Content is for:</span>
            {Object.entries(AUDIENCE).map(([k, label]) => (
              <label key={k} style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', fontSize: '13px' }}>
                <input type="checkbox" checked={card.audience.includes(k)} onChange={() => toggleAudience(k)} />{label}
              </label>
            ))}
          </div>
          <div className="form-row">
            <button onClick={() => save(card)} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="btn-secondary" onClick={() => { setEditing(false); setCard(cardOf(creator)); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="form-row" style={{ flexWrap: 'wrap' }}>
          <span className={facts.length ? 'sub' : 'empty'} style={{ fontSize: '13px' }}>
            {facts.length ? facts.join(' · ') : 'Blank: treated as a woman whose content is for straight men.'}
          </span>
          {canEdit && <button className="btn-secondary btn-small" onClick={() => setEditing(true)}>{facts.length ? 'Edit' : 'Fill in'}</button>}
          <button className="btn-secondary btn-small" onClick={readVault} disabled={reading}>{reading ? 'Reading the vault…' : 'From the vault'}</button>
        </div>
      )}
      {vault && (
        <div className="sub" style={{ fontSize: '13px', marginTop: '8px', paddingLeft: '10px', borderLeft: '2px solid #2a2a2a' }}>
          {vault.described < vault.needed ? (
            <div>Not enough in the vault yet: {vault.described} described photos, the card is filled from {vault.needed}+.</div>
          ) : <>
            {open.map(([k, sg]) => (
              <div key={k} className="form-row" style={{ alignItems: 'center' }}>
                <span>
                  {creator[k] ? '⚠ ' : ''}{FIELD_NAME[k]}: <strong>{SUGGEST_LABEL[k][sg.value]}</strong> — {sg.why}
                  {creator[k] ? ` (the card says ${SUGGEST_LABEL[k][creator[k]]})` : ''}
                </span>
                {canEdit && <button className="btn-secondary btn-small" disabled={saving} onClick={() => save({ [k]: sg.value })}>Use</button>}
              </div>
            ))}
            {!open.length && <div>The card agrees with the vault{Object.keys(vault.suggestions || {}).length ? '' : ' (nothing the vault is sure about)'}.</div>}
            {Object.keys(vault.from_vault || {}).length > 0 && (
              <div>Looks: {Object.entries(vault.from_vault).map(([k, v]) => `${k.replace('_', ' ')} ${v}`).join(' · ')}</div>
            )}
            <div style={{ color: '#666' }}>Age, who the content is for and solo / couple can't be read from photos.</div>
          </>}
          {vault.content_map && vault.content_map.niches.length > 0 && (
            <div>Content report niches: {vault.content_map.niches.join(', ')}{vault.content_map.appeal ? ` — ${vault.content_map.appeal}` : ''}</div>
          )}
        </div>
      )}
    </div>
  );
}

// The creator profile card: the only personal facts the Reply Engine's replies
// may use, so every account of this creator tells the same life.
function ProfileCard({ creator, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(creator.profile_card || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/creators/${creator.id}`, { profile_card: text });
      setEditing(false);
      onSaved();
    } catch (err) {
      notifyError(err, 'Could not save the profile card');
    }
    setSaving(false);
  };
  const cancel = () => { setEditing(false); setText(creator.profile_card || ''); };
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '12px', color: '#aaaaaa', marginBottom: '6px' }}>Life facts: the only personal facts Reply Engine replies may use</div>
      {editing ? (
        <div className="form-grid">
          <textarea rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="e.g. 24, lives in Austin, single, no kids, has a cat named Mochi, works from home, into the gym and thrifting" />
          <div className="form-row">
            <button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="btn-secondary" onClick={cancel}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="form-row">
          <span className={creator.profile_card ? 'sub' : 'empty'} style={{ fontSize: '13px' }}>{creator.profile_card || 'No profile card yet: replies may invent personal details.'}</span>
          {canEdit && <button className="btn-secondary btn-small" onClick={() => setEditing(true)}>{creator.profile_card ? 'Edit' : 'Add'}</button>}
        </div>
      )}
    </div>
  );
}

function Creators({ creators, selectedCreator, onSelectCreator, onUpdate, role, viewingOrgId }) {
  const [accounts, setAccounts] = useState([]);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/api/accounts`);
      setAccounts(res.data);
    } catch (err) {
      notifyError(err, 'Could not load accounts');
    }
  };

  const addCreator = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/creators`, { name: newName, organization_id: viewingOrgId });
      setNewName('');
      onUpdate();
    } catch (err) {
      notifyError(err, 'Could not add creator');
    }
    setLoading(false);
  };

  const saveEdit = async (id) => {
    if (!editingName.trim()) return;
    try {
      await axios.put(`${API}/api/creators/${id}`, { name: editingName });
      setEditingId(null);
      onUpdate();
    } catch (err) {
      notifyError(err, 'Could not rename creator');
    }
  };

  const deleteCreator = async (id) => {
    const creator = creators.find(c => c.id === id);
    if (creator?.name === 'Unassigned') {
      alert('The Unassigned creator cannot be deleted.');
      return;
    }
    if (!window.confirm('Delete this creator for good? Their accounts will become unassigned. A creator with post history cannot be deleted: archive it instead.')) return;
    try {
      await axios.delete(`${API}/api/creators/${id}`);
      if (selectedCreator?.id === id) onSelectCreator(null);
      onUpdate();
    } catch (err) {
      notifyError(err, 'Could not delete creator');
    }
  };

  // Archive: the creator and their accounts leave the lists and the daily
  // jobs, all history stays; Restore brings them back
  const archiveCreator = async (id, archived) => {
    const creator = creators.find(c => c.id === id);
    if (archived && !window.confirm(`Archive ${creator?.name}? They and their accounts leave the lists and the daily jobs (click sync, Monday check, report cards). All history stays, and you can restore them here.`)) return;
    try {
      await axios.post(`${API}/api/creators/${id}/archive`, { archived });
      if (archived && selectedCreator?.id === id) onSelectCreator(null);
      onUpdate();
    } catch (err) {
      notifyError(err, archived ? 'Could not archive creator' : 'Could not restore creator');
    }
  };

  const assignAccount = async (accountId, creatorId) => {
    try {
      // If unassigning, assign to "Unassigned" creator instead of null
      let targetCreatorId = creatorId;
      if (!creatorId) {
        const unassigned = creators.find(c => c.name === 'Unassigned');
        targetCreatorId = unassigned?.id || null;
      }
      await axios.put(`${API}/api/accounts/${accountId}`, { creator_id: targetCreatorId });
      fetchAccounts();
      onUpdate();
    } catch (err) {
      notifyError(err, 'Could not assign account');
    }
  };

  const activeCreators = creators.filter(c => !c.archived_at);
  const archivedCreators = creators.filter(c => c.archived_at);
  const unassignedCreator = creators.find(c => c.name === 'Unassigned');
  const unassignedAccounts = accounts.filter(a => !a.creator_id || a.creator_id === unassignedCreator?.id);

  return (
    <div className="page">
      <h1>Creators</h1>
      <p>Manage creators and assign Reddit accounts to them.</p>

      {(role === 'super_admin' || role === 'admin') && (
        <div className="card">
          <h2>Add Creator</h2>
          <div className="form-row">
            <input
              placeholder="Creator name (e.g. PetiteMoonie)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyPress={e => { if (e.key === 'Enter') addCreator(); }}
            />
            <button onClick={addCreator} disabled={loading}>
              {loading ? 'Adding...' : 'Add Creator'}
            </button>
          </div>
        </div>
      )}

      {activeCreators.length === 0 ? (
        <div className="card">
          <p className="empty">No creators yet. Add one above.</p>
        </div>
      ) : (
        activeCreators.map(creator => (
          <div className="card" key={creator.id} style={{
            borderColor: selectedCreator?.id === creator.id ? '#ff4500' : '#2a2a2a'
          }}>
            <div className="list-header">
              <div>
                {editingId === creator.id ? (
                  <div className="form-row">
                    <input
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                    />
                    <button onClick={() => saveEdit(creator.id)}>Save</button>
                    <button className="btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                ) : (
                  <strong style={{ fontSize: '16px' }}>{creator.name}</strong>
                )}
                <span className="sub"> · {(creator.accounts || []).length} accounts</span>
              </div>
              <div className="list-item-actions">
                <button
                  onClick={() => onSelectCreator(selectedCreator?.id === creator.id ? null : creator)}
                  style={{
                    padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    fontSize: '13px', fontWeight: '600',
                    background: selectedCreator?.id === creator.id ? '#ff4500' : '#2a2a2a',
                    color: selectedCreator?.id === creator.id ? '#ffffff' : '#aaaaaa'
                  }}
                >
                  {selectedCreator?.id === creator.id ? '✓ Selected' : 'Select'}
                </button>
                {(role === 'super_admin' || role === 'admin') && (
                <>
                  {creator.name !== 'Unassigned' && (
                    <button className="btn-secondary" onClick={() => { setEditingId(creator.id); setEditingName(creator.name); }}>Rename</button>
                  )}
                  {creator.name !== 'Unassigned' && (
                    <button className="btn-secondary" onClick={() => archiveCreator(creator.id, true)}>Archive</button>
                  )}
                  {creator.name !== 'Unassigned' && (
                    <button className="btn-danger" onClick={() => deleteCreator(creator.id)}>Delete</button>
                  )}
                </>
              )}
              </div>
            </div>

            {creator.name !== 'Unassigned' && <>
              <CreatorCard key={creator.id} creator={creator} canEdit={role !== 'poster_va'} onSaved={onUpdate} />
              <ProfileCard key={`${creator.id}-${creator.profile_card || ''}`} creator={creator} canEdit={role !== 'poster_va'} onSaved={onUpdate} />
            </>}

            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', color: '#aaaaaa', marginBottom: '8px' }}>Assigned accounts:</div>
              {(creator.accounts || []).length === 0 ? (
                <p className="empty" style={{ fontSize: '13px' }}>No accounts assigned yet.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {(creator.accounts || []).map(account => (
                    <span key={account.id} style={{
                      background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '20px',
                      padding: '4px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                      u/{account.reddit_username}
                      <span
                        onClick={() => assignAccount(account.id, null)}
                        style={{ cursor: 'pointer', color: '#ff4500', fontWeight: '700' }}
                      >×</span>
                    </span>
                  ))}
                </div>
              )}
              {unassignedAccounts.length > 0 && creator.name !== 'Unassigned' && (
                <div className="form-row">
                  <select
                    defaultValue=""
                    onChange={e => { if (e.target.value) { assignAccount(e.target.value, creator.id); e.target.value = ''; } }}
                  >
                    <option value="">+ Assign account</option>
                    {unassignedAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name} (u/{a.reddit_username})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {archivedCreators.length > 0 && (
        <div className="card">
          <h2>Archived</h2>
          <p className="sub">Out of the lists and the daily jobs; all their history is kept.</p>
          {archivedCreators.map(creator => (
            <div className="list-header" key={creator.id} style={{ marginTop: '8px' }}>
              <div>
                <strong>{creator.name}</strong>
                <span className="sub"> · {(creator.accounts || []).map(a => `u/${a.reddit_username}`).join(', ') || 'no accounts'}</span>
                <span className="sub"> · archived {new Date(creator.archived_at).toLocaleDateString()}</span>
              </div>
              {(role === 'super_admin' || role === 'admin') && (
                <div className="list-item-actions">
                  <button className="btn-secondary" onClick={() => archiveCreator(creator.id, false)}>Restore</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Creators;